import { registeredFieldSelectionId } from '../../../domains/knowledge-ingestion/services/SpreadsheetIntentResolver';
import type {
  SpreadsheetIntentCandidate,
  SpreadsheetMappingReviewSelection,
  SpreadsheetIntentResolution,
} from '../../../domains/knowledge-ingestion/services/SpreadsheetIntentResolver';

/**
 * Which intent the review UI starts on.
 *
 * A blank slate made a confidently-detected import look undetected: the
 * resolver would rank "Person hierarchy" at 98% and still force a manual pick
 * because the runner-up sat 8 points behind. So a near-tie now *defaults* to
 * the top candidate — the resolver recognised the shape, it just had a close
 * second, and the review panel carries that doubt in a confirm banner.
 *
 * The blank slate is kept for the cases where the resolver is genuinely saying
 * "I cannot tell what this is": `generic_table`, sub-threshold confidence, or a
 * profile that explicitly demands review. Defaulting those would import junk
 * under a confident-looking label.
 */
export function initialSelectedIntentForResolution(
  resolution: SpreadsheetIntentResolution,
): string | null {
  if (!resolution.requiresUserConfirmation) return resolution.recommended.intent;
  const recognizedShape =
    resolution.recommended.intent !== 'generic_table' &&
    !resolution.recommended.mappingProfile.requiresReview;
  // `hasNearTie` implies the runner-up cleared the confidence floor, so the
  // recommendation cleared it too — no separate confidence check needed.
  return resolution.hasNearTie && recognizedShape ? resolution.recommended.intent : null;
}

export function findSelectedIntentCandidate(
  resolution: SpreadsheetIntentResolution | null,
  selectedIntent: string | null,
): SpreadsheetIntentCandidate | null {
  if (!resolution) return null;
  if (!selectedIntent) return resolution.recommended;
  return resolution.candidates.find((candidate) => candidate.intent === selectedIntent) ?? null;
}

export function canConfirmIntentResolution(
  resolution: SpreadsheetIntentResolution | null,
  selectedIntent: string | null,
): boolean {
  if (!resolution) return false;
  if (!selectedIntent) return false;
  return resolution.candidates.some((candidate) => candidate.intent === selectedIntent);
}

export function initialMappingSelections(
  candidate: SpreadsheetIntentCandidate,
): SpreadsheetMappingReviewSelection {
  return Object.fromEntries(
    [
      ...candidate.reviewFields.map((field) => [field.id, field.sourceField] as const),
      ...candidate.registeredFields.map((field) => [
        registeredFieldSelectionId(field.targetField),
        field.sourceField,
      ] as const),
    ],
  );
}

export function canConfirmMappingSelections(
  candidate: SpreadsheetIntentCandidate | null,
  selections: SpreadsheetMappingReviewSelection,
): boolean {
  if (!candidate) return false;
  return candidate.reviewFields.every(
    (field) => !field.required || Boolean(selections[field.id]),
  );
}
